import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HiPrinter,
  HiArrowLeft,
  HiSearch
} from 'react-icons/hi';
import Select from './Select';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import { useNotification } from '../hooks/useNotification';
import './Labels.css';

const Labels = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showWarning } = useNotification();
  const [boxes, setBoxes] = useState([]);
  const [selectedBoxId, setSelectedBoxId] = useState('');
  const [box, setBox] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBoxes();
  }, []);

  // Ao carregar as caixas, preencher o select com o box da URL (ex: /labels?box=10) e já gerar a etiqueta
  useEffect(() => {
    const boxIdFromUrl = searchParams.get('box');
    if (boxIdFromUrl && Array.isArray(boxes) && boxes.length > 0 && !selectedBoxId) {
      const exists = boxes.some(b => String(b.id) === String(boxIdFromUrl));
      if (exists) {
        setSelectedBoxId(String(boxIdFromUrl));
      }
    }
  }, [boxes, searchParams, selectedBoxId]);

  useEffect(() => {
    if (selectedBoxId && Array.isArray(boxes) && boxes.length > 0) {
      loadBox();
    }
  }, [selectedBoxId, boxes]);

  const loadBoxes = async () => {
    try {
      const data = await apiService.getBoxes();
      const boxesList = normalizeApiData(data);
      // Mapear os campos da API para o formato esperado pelo componente
      const boxesWithDefaults = boxesList.map(box => ({
        ...box,
        // Mapear codigo_pacote para codigo
        codigo: box.codigo_pacote || box.codigo || '',
        // Mapear cliente.nome para cliente_nome
        cliente_nome: box.cliente?.nome || box.cliente_nome || '',
        // Mapear cliente_id
        cliente_id: box.cliente_id || box.cliente?.id || '',
        // Mapear dimensões separadas para objeto dimensoes
        dimensoes: {
          comprimento: box.comprimento || box.dimensoes?.comprimento || '',
          largura: box.largura || box.dimensoes?.largura || '',
          altura: box.altura || box.dimensoes?.altura || ''
        },
        // Mapear peso_bruto para peso
        peso: box.peso_bruto || box.peso || '',
        // Mapear valor_declarado para valor_remessa
        valor_remessa: box.valor_declarado || box.valor_remessa || '',
        // Preservar status da API
        status: box.status || 'pedido_criado'
      }));
      setBoxes(boxesWithDefaults);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar caixas:', error);
      setBoxes([]);
      setLoading(false);
    }
  };

  const loadBox = async () => {
    if (selectedBoxId && Array.isArray(boxes)) {
      try {
        const foundBox = boxes.find(b => b.id === Number(selectedBoxId) || b.codigo === selectedBoxId);
        if (foundBox && foundBox.id) {
          try {
            const labelData = await apiService.getLabel(foundBox.id);
            setBox(labelData.box || foundBox);
            if (labelData.invoice) {
              setInvoice(labelData.invoice);
            }
          } catch (labelError) {
            // Se o erro for 500 ou outro erro do servidor, usar os dados do box encontrado
            const isServerError = labelError.status === 500 ||
                                 labelError.isServerError ||
                                 (labelError.response && labelError.response.status === 500) ||
                                 (labelError.message && labelError.message.includes('Server Error'));

            if (isServerError) {
              console.warn('Erro ao carregar dados da etiqueta do servidor, usando dados locais:', labelError);
              // Usar os dados do box encontrado como fallback
              setBox(foundBox);
              setInvoice(null);
              showWarning('Não foi possível carregar os dados completos da etiqueta. Exibindo informações básicas.');
            } else {
              // Para outros erros (404, 403, etc), também usar fallback mas com mensagem diferente
              console.warn('Erro ao carregar etiqueta:', labelError);
              setBox(foundBox);
              setInvoice(null);
              if (labelError.status === 404) {
                showWarning('Etiqueta não encontrada no servidor. Exibindo informações básicas.');
              } else {
                showWarning('Não foi possível carregar os dados completos da etiqueta. Exibindo informações básicas.');
              }
            }
          }
        } else {
          setBox(null);
          setInvoice(null);
        }
      } catch (error) {
        console.error('Erro ao carregar etiqueta:', error);
        showWarning('Erro ao carregar informações da etiqueta. Por favor, tente novamente.');
        setBox(null);
        setInvoice(null);
      }
    } else {
      setBox(null);
      setInvoice(null);
    }
  };

  const handlePrint = () => {
    // Obter código de verificação (prioridade: codigo_rastreio > codigo)
    const codigoVerificacao = box?.codigo_rastreio || box?.codigo || 'SEM-CODIGO';

    // Salvar título original
    const originalTitle = document.title;

    // Definir título com código de verificação para ser usado no nome do arquivo ao salvar
    document.title = `Etiqueta_${codigoVerificacao}_${box?.codigo || 'ETIQUETA'}`;

    // Adicionar listener para restaurar título após impressão
    const restoreTitle = () => {
      document.title = originalTitle;
      window.removeEventListener('afterprint', restoreTitle);
    };

    window.addEventListener('afterprint', restoreTitle);

    // Chamar impressão
    window.print();
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '2rem' }}>
          <div className="loading-spinner-large"></div>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Carregando etiquetas...</p>
        </div>
      </div>
    );
  }

  const handleBoxChange = (e) => {
    const value = e.target.value;
    setSelectedBoxId(value);
    if (value) {
      navigate(`/labels?box=${value}`, { replace: true });
    } else {
      navigate('/labels', { replace: true });
    }
  };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Caixas / Malas</span>
        <span>Etiqueta</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Etiqueta de Envio</h1>
          <p>Selecione uma caixa/mala para gerar a etiqueta</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/boxes')}>
            <HiArrowLeft size={18} />
            Voltar
          </button>
        </div>
      </div>

      <div className="label-selector-section">
        <div className="form-group">
          <label>
            <HiSearch size={18} />
            Selecionar Caixa/Mala
          </label>
          <Select
            value={selectedBoxId}
            onChange={handleBoxChange}
            placeholder="Selecione uma caixa/mala"
            options={Array.isArray(boxes) ? boxes.map(b => ({
                value: String(b.id),
                label: `${b.codigo} - ${b.cliente_nome} (${b.tipo === 'caixa' ? 'Caixa' : 'Mala'})`
            })) : []}
          />
        </div>
      </div>

      {!box && selectedBoxId && (
        <div className="empty-state">
          <p>Caixa/Mala não encontrada</p>
        </div>
      )}

      {!selectedBoxId && (
        <div className="empty-state">
          <p>Selecione uma caixa/mala para visualizar a etiqueta</p>
        </div>
      )}

      {box && (
        <>
          <div className="page-header">
            <div>
              <h2>Etiqueta - {box.codigo}</h2>
              <p>Cliente: {box.cliente_nome}</p>
            </div>
            <div className="header-actions">
              <button className="btn btn-primary" onClick={handlePrint}>
                <HiPrinter size={18} />
                Imprimir
              </button>
            </div>
          </div>

      <div className="label-container">
        <div className="label-paper">
          {/* Cabeçalho com Logo e Nome da Empresa */}
          <div className="label-top-header">
            <div className="label-company-info">
              <img
                src="/SENDING_TO.png"
                alt="SendingTo Express"
                className="label-logo-image"
              />
              <div className="label-company-name">
                <h1 className="label-company-title">SendingTo Express</h1>
                <p className="label-subtitle">Serviço de Envio Internacional</p>
              </div>
            </div>
          </div>

          <div className="label-content">
            <div className="label-section label-section-compact">
              <h3>Cliente</h3>
              <div className="label-field">
                <strong>Nome:</strong> {invoice?.cliente_destinatario || invoice?.clientName || box.cliente_nome || 'Não informado'}
              </div>
              {invoice?.email_cliente && (
                <div className="label-field">
                  <strong>Email:</strong> {invoice.email_cliente}
                </div>
              )}
              {invoice?.telefone_cliente && (
                <div className="label-field">
                  <strong>Telefone:</strong> {invoice.telefone_cliente}
                </div>
              )}
              {(invoice?.endereco || invoice?.cliente_endereco) && (
                <div className="label-field">
                  <strong>Endereço:</strong> {invoice.endereco || invoice.cliente_endereco}
                  {invoice?.cliente_numero && `, ${invoice.cliente_numero}`}
                </div>
              )}
              {invoice?.cliente_cep && (
                <div className="label-field">
                  <strong>CEP:</strong> {invoice.cliente_cep}
                </div>
              )}
              {(invoice?.cliente_cidade || invoice?.cliente_estado) && (
                <div className="label-field">
                  <strong>Cidade/Estado:</strong> {invoice.cliente_cidade || ''}{invoice.cliente_cidade && invoice.cliente_estado ? '/' : ''}{invoice.cliente_estado || ''}
                </div>
              )}
              {invoice?.cliente_pais && (
                <div className="label-field">
                  <strong>País:</strong> {invoice.cliente_pais}
                </div>
              )}
            </div>

            <div className="label-section label-section-compact label-section-route">
              <h3>Dados da Rota</h3>
              <div className="label-field label-field-large">
                <strong>Origem:</strong> {box.origem || invoice?.origem || 'Não informado'}
              </div>
              <div className="label-field label-field-large">
                <strong>Destino:</strong> {box.destino || invoice?.destino || 'Não informado'}
              </div>
              <div className="label-field label-field-large">
                <strong>País de Trânsito:</strong> {box.pais_transito || 'Não informado'}
              </div>
            </div>

            <div className="label-section label-section-compact label-section-dimensions">
              <h3>Dimensões e Peso</h3>
              <div className="label-field">
                <strong>Peso:</strong> {box.peso}
              </div>
              <div className="label-field">
                <strong>Dimensões:</strong> {box.dimensoes?.comprimento || '-'} x {box.dimensoes?.largura || '-'} x {box.dimensoes?.altura || '-'}
              </div>
              <div className="label-field">
                <strong>Tipo de Pacote:</strong> {box.tipo === 'caixa' ? 'Caixa' : 'Mala'}
              </div>
              <div className="label-barcode">
                <div className="barcode-placeholder">
                  {box.codigo}
                </div>
              </div>
            </div>
          </div>

          <div className="label-footer">
            <p><strong>IMPORTANTE:</strong> Mantenha esta etiqueta visível durante todo o transporte.</p>
            <p>Em caso de dúvidas, entre em contato: contato@sendingto.com</p>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Labels;
